import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../../components/buttons/button";
import { NumberCard } from "../../../components/cards/numberCard";
import { ProfileIcon } from "../../../components/cards/profilePic";
import Dropdown from "../../../components/dropdown/dropdown";
import { Checkbox } from "../../../components/inputs/checkbox";
import { SearchInput } from "../../../components/inputs/searchInput";
import { Pill } from "../../../components/pill";
import { TableComponent } from "../../../components/table";
import { TabNav } from "../../../components/tabNav";
import { TabSelect } from "../../../components/tabNav/tabSelect";
import { PageHeader } from "../../../components/typography/pageHeader";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useGetAllUsersQuery } from "../../../redux/features/admin/users/users.api";
import { GetAllUsersQuery } from "../../../redux/features/admin/users/users.types";
import { useExportDataMutation } from "../../../redux/features/misc/misc.api";
import { userStatusPillMap } from "../../../utils/constants";
import { UserStatus } from "../../../utils/types";
import { UserType } from "../../../redux/features/auth/auth.types";

const userStatuses: {
  title: string;
  value?: UserStatus | boolean;
  type: "subAccountIsVerified" | "userStatus";
}[] = [
  {
    title: "Active",
    value: "active",
    type: "userStatus",
  },
  {
    title: "No Account",
    value: "inactive",
    type: "userStatus",
  },
  {
    title: "Suspended",
    value: "suspended",
    type: "userStatus",
  },
  {
    title: "Unverified Sub Accounts",
    value: undefined,
    type: "subAccountIsVerified",
  },
];

export const UsersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") as UserType;

  const [query, setQuery] = useState<GetAllUsersQuery>({
    page: 1,
    limit: 50,
    search: undefined,
    sortOrder: "desc",
    status: undefined,
    subAccountIsVerified: undefined,
  });
  const [statusFilter, setStatusFilter] = useState<string>();
  const [subAccStatus, setSubAccStatus] = useState<boolean | undefined>();

  const navigate = useNavigate();

  const tabs = [
    {
      title: "All",
      to: "all",
      onClick: () => {
        setSearchParams({});
      },
    },
    {
      title: "Guests",
      to: "guest",
      onClick: () => {
        setSearchParams({
          tab: "guest",
        });
      },
    },
    {
      title: "Organizers",
      to: "organiser",
      onClick: () => {
        setSearchParams({
          tab: "organiser",
        });
      },
    },
  ];

  const [exportData, { isLoading: exportingData }] = useExportDataMutation();

  const queryParams = useMemo(() => {
  const userType = activeTab ? activeTab : undefined;

    return {
      ...query,
      search: query.search === "" ? undefined : query.search,
      userType: userType,
    };
    // eslint-disable-next-line
  }, [
    query.page,
    query.limit,
    query.search,
    query.sortOrder,
    query.status,
    query.userType,
    query.subAccountIsVerified,
    activeTab,
  ]);

  const {
    isFetching: fetchingUsers,
    isLoading: loadingUsers,
    data: usersData,
  } = useGetAllUsersQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  return (
    <div className="w-full">
      <PageHeader header={"Users"} className="font-unbounded font-normal" />

      <TabNav
        navs={tabs}
        className="mt-8 !min-w-full md:!min-w-fit md:!max-w-fit"
        tabClassName=""
        activeTab={activeTab ?? "all"}
        variant="switcher"
        theme="plain"
      />

      <div className="mt-3 flex flex-col gap-[2px]">
        <BgWrapper className="grid grid-cols-1 gap-[10px] rounded-b-none md:grid-cols-3">
          <NumberCard
            value={usersData?.data?.stats?.total ?? 0}
            text="Total Users"
            isCurrency={false}
            loading={loadingUsers}
            titleFirst
          />

          <NumberCard
            value={usersData?.data?.stats?.guest ?? 0}
            text="Total Guests"
            isCurrency={false}
            loading={loadingUsers}
            titleFirst
          />

          <NumberCard
            value={usersData?.data?.stats?.organiser ?? 0}
            text="Total Organizers"
            isCurrency={false}
            loading={loadingUsers}
            titleFirst
          />
        </BgWrapper>

        <BgWrapper className="flex flex-col gap-3 rounded-t-none">
          <div className="flex w-full flex-col items-center justify-between gap-4 md:flex-row">
            <SearchInput
              searchFunction={(value) => {
                setQuery({
                  ...query,
                  search: value,
                  page: 1,
                });
              }}
              placeholder="Search"
              className="md:!max-w-[300px]"
            />

            <div className="grid w-full grid-cols-2 items-center gap-3 md:flex md:w-fit md:flex-row">
              <Dropdown
                className="order-last col-span-full md:order-first"
                position="bottom start"
                closeOnClick={false}
                customMenuItems={({ close, open }) => {
                  if (!open && statusFilter !== query.status) {
                    setStatusFilter(query.status);
                  }
                  if (!open && subAccStatus !== query.subAccountIsVerified) {
                    setSubAccStatus(query.subAccountIsVerified);
                  }

                  return (
                    <div className="flex flex-col rounded-xl border border-grey-200 bg-white">
                      {userStatuses.map((userStatus) => {
                        return (
                          <Checkbox
                            checkStatus={
                              (userStatus.type === "userStatus" &&
                                (statusFilter?.split(",") ?? [])?.includes(
                                  userStatus.value as string,
                                )) ||
                              (userStatus.type === "subAccountIsVerified" &&
                                subAccStatus === false)
                            }
                            text={userStatus.title}
                            handleChange={(checked) => {
                              if (userStatus.type === "userStatus") {
                                const statusFilters =
                                  statusFilter?.split(",") ?? [];

                                if (checked) {
                                  statusFilters.push(
                                    userStatus.value as string,
                                  );
                                  setStatusFilter(
                                    statusFilters.join(",") || undefined,
                                  );
                                } else {
                                  const filteredStatusFilters =
                                    statusFilters.filter(
                                      (i) => i !== userStatus.value,
                                    );

                                  setStatusFilter(
                                    filteredStatusFilters.join(",") ||
                                      undefined,
                                  );
                                }
                              } else {
                                setSubAccStatus(checked ? false : undefined);
                              }
                            }}
                            className="w-full border-b p-3"
                            key={`${userStatus.value}`}
                          />
                        );
                      })}

                      <div className="w-full p-2">
                        <Button
                          type="button"
                          className="w-full"
                          variation="primary"
                          text="Apply"
                          onClick={() => {
                            setQuery({
                              ...query,
                              status: statusFilter,
                              subAccountIsVerified: subAccStatus,
                              page: 1,
                            });
                            close();
                          }}
                        />
                      </div>
                    </div>
                  );
                }}
              >
                <Button
                  type="button"
                  onClick={() => {}}
                  text="Filter"
                  className="!min-w-fit !rounded-2xl !p-3 !text-sm"
                  icon={"filter"}
                  variation="grey"
                />
              </Dropdown>

              <Button
                type="button"
                onClick={() => {
                  if (query.sortOrder === "asc") {
                    setQuery({ ...query, sortOrder: "desc" });
                  } else {
                    setQuery({ ...query, sortOrder: "asc" });
                  }
                }}
                text="Date Modified"
                className="!min-w-fit !rounded-2xl !p-3 !text-sm"
                icon={query.sortOrder === "asc" ? "order-asc" : "order-desc"}
                variation="grey"
              />

              <Button
                type="button"
                onClick={async () => {
                  await exportData({
                    type: "users",
                    fetchAll: true,
                    sortOrder: query.sortOrder,
                    search: query.search || undefined,
                    searchTerm: query.search || undefined,
                  });
                }}
                text="Export"
                className="!min-w-fit !rounded-2xl !p-3 !text-sm"
                icon="export"
                variation="grey"
                loading={exportingData}
              />
            </div>
          </div>

          {(query.status !== undefined ||
            query.subAccountIsVerified !== undefined) && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-base font-bold text-grey-600">
                {usersData?.data?.totalCount} User
                {(usersData?.data?.totalCount ?? 0) > 1 && "s"}
              </p>
              <div className="flex flex-wrap items-center justify-end">
                <TabSelect
                  tabClassName="capitalize"
                  tabs={[
                    ...(query?.status?.split(",") ?? []).map((s) => {
                      return {
                        title: s,
                        to: s,
                        onClick: () => {},
                        onRemove: (val: string) => {
                          setQuery({
                            ...query,
                            status:
                              (query?.status?.split(",") ?? [])
                                .filter((i) => i !== val)
                                ?.join(",") || undefined,
                          });
                        },
                      };
                    }),
                    query.subAccountIsVerified === false
                      ? {
                          title: "Unverified Sub Accounts",
                          to: "",
                          onClick: () => {},
                          onRemove: () => {
                            setQuery({
                              ...query,
                              subAccountIsVerified: undefined,
                            });
                          },
                        }
                      : undefined,
                  ]}
                />
              </div>
            </div>
          )}

          <TableComponent
            headers={[]}
            rows={
              usersData?.data?.users?.map((user, idx) => {
                return {
                  id: idx,
                  items: [
                    <div
                      className={`flex w-full flex-1 flex-col gap-2 md:flex-row md:items-center`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <ProfileIcon img="" dimension={32} className="" />
                      </div>

                      <p className="flex flex-1 flex-wrap items-center gap-0 text-sm text-grey-600">
                        <span className="font-bold text-grey-900">
                          {user.name}
                        </span>
                        <span className={` `}>∙</span>
                        <span className={``}>{user.email}</span>
                      </p>

                      <div className="flex flex-row flex-wrap items-center gap-2">
                        <Pill
                          text={
                            user.userType === "guest" ? "GUEST" : "ORGANIZER"
                          }
                          variation={
                            user.userType === "guest" ? "blue" : "purple"
                          }
                        />
                        {user.isOrganisation && (
                          <Pill text={"COMPANY"} variation={"grey"} />
                        )}
                        {!user.subAccountIsVerified &&
                        user.userType !== "guest" ? (
                          <Pill text={"UNVERIFIED SA"} variation={"grey"} />
                        ) : undefined}
                        <Pill
                          text={userStatusPillMap[user.status]?.text}
                          variation={userStatusPillMap[user.status]?.variant}
                        />
                      </div>
                    </div>,
                  ],
                };
              }) ?? []
            }
            loadingRows={query.limit}
            isLoading={fetchingUsers}
            isRowClickable
            onRowClick={(idx) => {
              navigate(`/users/${usersData?.data?.users[idx]?._id}`);
            }}
            isTableEmpty={[0, undefined].includes(
              usersData?.data?.users?.length,
            )}
            emptyTitle="No Users"
            emptySubTitle=""
            emptyImage="/assets/images/guest-glass.png"
            isPaginated
            currentPage={query.page ?? 1}
            totalCount={usersData?.data?.stats?.total ?? 0}
            itemsPerPage={query.limit!}
            pageRangeDisplayed={3}
            onPageChange={(page) => {
              setQuery({
                ...query,
                page: page,
              });
            }}
            className=""
          />
        </BgWrapper>
      </div>
    </div>
  );
};
